import { useCallback, useState, useMemo, useEffect } from 'react';
import { updateVipState, useVipState } from '@shakkerai/pro-store';
import { useTempState, useProjectState } from '@starflow/app/store';
import { getElementGenInfo, useLoadingHooks } from '@shakkerai/pro-hooks';
import { requestVipPurchaseList, syncUserVipInfo, requestAccelerate } from '@shakkerai/services';
import { notifyError, notifySuccess } from '@shakkerai/pro-components';
import { getCurrentGenerateParams, prepareGenerateParams } from '@starflow/app/modules/generator/helpers/generate';
import { calculatePower } from '@shakkerai/services/src/vip';
import { useDebounceFn } from 'ahooks';
import { getActiveGeneratorNodeId } from '@starflow/app/helpers/flow';

export const usePurchaseHooks = () => {
  const {
    isLoading,
    isLoadSuccess,
    isLoadFailed,

    setLoadingState,
    setSuccessState,
    setFailState,
  } = useLoadingHooks();

  const [purchaseList, setPurchaseList] = useState([]);

  /**
   * 加载可购买的套餐列表
   */
  const loadPurchasesList = useCallback(async () => {
    setLoadingState();
    try {
      const data = await requestVipPurchaseList();
      setSuccessState();
      setPurchaseList(data);
    } catch (error) {
      setFailState();
      notifyError(error.message);
    }
    // eslint-disable-next-line
  }, []);

  return {
    isLoading,
    isLoadSuccess,
    isLoadFailed,

    loadPurchasesList,
    purchaseList,
  };
};

/**
 * 同步会员信息
 */
export const syncVipInfo = async () => {
  const data = await syncUserVipInfo();
  updateVipState({
    userVipInfo: data,
  });
};

export const useUserVipInfo = () => {
  const userVipInfo = useVipState('userVipInfo');

  /**
   * 加载用户会员信息
   */
  const loadVipInfo = useCallback(syncVipInfo, []);

  /**
   * 剩余算力
   */
  const remainedPower = useMemo(() => {
    return userVipInfo?.attr?.totalPower - userVipInfo?.attr?.usedPower || 0;
  }, [userVipInfo]);

  return { userVipInfo, loadVipInfo, remainedPower };
};

/**
 * 计算用户的身份状态
 *
 * @param accountInfo
 */
export const useUserIdentificationHooks = (accountInfo) => {
  // 计算用户当前身份: 普通用户1、基础版2、专业版3
  const accountLevel = useMemo(() => {
    if (accountInfo?.effective) {
      return accountInfo.accountLevel;
    } else {
      // 普通用户
      return 1;
    }
  }, [accountInfo]);

  // 计算当前账户是月包还是年包还是季包, 月包1,年包2,季包3
  const accountExpire = useMemo(() => {
    return accountInfo?.type || 1;
  }, [accountInfo]);

  // 计算当前账户的套餐时长类型文案：连续包月/12个月
  const accountDurationText = useMemo(() => {
    switch (accountExpire) {
      case 1:
        // 连续包月的情况不展示字样
        return '';
      case 4:
        return '12个月';
      default:
        return '';
    }
  }, [accountExpire]);

  // 是否普通用户
  const isOrdinaryUser = useMemo(() => {
    return accountLevel === 1;
  }, [accountLevel]);

  /**
   * 是否是会员
   */
  const isVip = useMemo(() => {
    return !isOrdinaryUser;
  }, [isOrdinaryUser]);

  // 基础月包用户
  const isBaseMonthUser = useMemo(() => {
    return accountLevel === 2 && accountExpire === 1;
  }, [accountLevel, accountExpire]);

  // 基础年包用户
  const isBaseYearUser = useMemo(() => {
    return accountLevel === 2 && accountExpire === 2;
  }, [accountLevel, accountExpire]);

  // 专业月包用户
  const isSpecialMonthUser = useMemo(() => {
    return accountLevel === 3 && accountExpire === 1;
  }, [accountLevel, accountExpire]);

  // 专业年包用户
  const isSpecialYearUser = useMemo(() => {
    return accountLevel === 3 && accountExpire === 2;
  }, [accountLevel, accountExpire]);

  // 是专业用户
  const isSpecialUser = useMemo(() => {
    return accountInfo?.effective && accountInfo?.accountLevel === 3;
  }, [accountInfo]);

  // 剩余加速次数
  const remainedPriority = useMemo(() => {
    if (accountInfo?.effective) {
      return accountInfo.attr.totalPriority - accountInfo.attr.usedPriority;
    } else {
      return 0;
    }
  }, [accountInfo]);

  return {
    accountLevel,
    accountExpire,
    accountDurationText,
    isVip,
    isOrdinaryUser,
    isBaseMonthUser,
    isBaseYearUser,
    isSpecialMonthUser,
    isSpecialYearUser,
    remainedPriority,
    isSpecialUser,
  };
};

// type定义在：packages/pro-panel/src/components/image/index.jsx
const PanelNameTypeMap = {
  inpaint: 'inpaint',
  outpaint: 'outpaint',
  upscale: 'upscale',
  collage: 'collage',
  changeFace: 'changeFace',
};

const calcCredits = async (element, count) => {
  try {
    const genInfo = await getElementGenInfo(element);
    const params = await prepareGenerateParams({
      type: 'generator',
      params: { ...genInfo, count, loraModels: [], sourceElement: element },
      isTryClacMode: true,
    });

    const response = await calculatePower({ ...params, projectData: null });

    return response.power || 0;
  } catch (error) {
    console.log(`[DEBUG] calcCredits`, error);
    return 0;
  }
};

const calcPromises = {};
const calcResults = {};

// 以element.id为key，避免同时发起计算请求
const scheduleCreditsCalc = async (element, count) => {
  const key = `${element.id}-${count}`;

  if (calcResults[key]) {
    return calcResults[key];
  }

  if (calcPromises[key]) {
    return calcPromises[key];
  }

  calcPromises[key] = calcCredits(element, count);

  calcPromises[key].then((credits) => {
    calcResults[key] = credits;
  });

  return calcPromises[key];
};

export const useVaryCredits = (element, count) => {
  const { remainedPower } = useUserVipInfo();
  const [credits, setCredits] = useState(-1);
  const insfficient = credits > remainedPower;

  useEffect(() => {
    if (element.innerTaskId) {
      scheduleCreditsCalc(element, count).then(setCredits);
    }
  }, [element, count]);

  return { credits, insfficient };
};

export const useCalcPointHooksV2 = () => {
  const { remainedPower } = useUserVipInfo();

  const preconsumedPower = useVipState('preconsumedPower');
  // 算力是否足够
  const isEnoughForClac = useMemo(() => {
    return remainedPower >= preconsumedPower;
  }, [remainedPower, preconsumedPower]);

  return {
    isEnoughForClac,
  };
};

/**
 * 参数变化监听和算力重新计算
 */
export const useCalcPointHooks = () => {
  const canvasState = useProjectState((state) => {
    // return state.generators;
    return state.nodeMap;
  });

  const elementState = useProjectState((state) => {
    return state.elements;
  });

  const selectionElements = useProjectState((state) => {
    return state.selection;
  });

  const topImagesTool = useProjectState('currentElementTool');

  const currentNodeId = useProjectState('currentNodeId');

  // 合页展开的时候就需要计算
  const { run: recalcPoint } = useDebounceFn(
    async () => {
      try {
        const params = await getCurrentGenerateParams(currentNodeId);

        // 算力计算
        if (params) {
          const response = await calculatePower(params);
          /**
           * type: 生图任务算力类型
           * 0 算力消耗模式
           * 1 无限生图模式
           */
          const { power, type = 0 } = response;

          // 存储算力
          updateVipState({
            preconsumedPower: power,
            showPowerFlag: type === 0,
          });
        }
      } catch (error) {
        console.info(`计算算力错误`, error);
      }
    },
    {
      wait: 500,
    },
  );

  useEffect(() => {
    if (currentNodeId && currentNodeId.startsWith('node-workflow')) {
      // 封装工作流算力计算暂时还没在这里
      return;
    }

    recalcPoint();
    // eslint-disable-next-line
  }, [canvasState, elementState, recalcPoint, currentNodeId, selectionElements.length]);
};

/**
 * 计算Pic商业化的排队中的状态文案
 */
export const usePicQueueHooks = (taskId) => {
  const { userVipInfo, loadVipInfo } = useUserVipInfo();

  const { isVip, remainedPriority, isSpecialUser } = useUserIdentificationHooks(userVipInfo);

  let btnText = useMemo(() => {
    if (isVip) {
      if (remainedPriority) {
        return '插队加速';
      } else {
        if (isSpecialUser) {
          return ''; // 次数用完，不展示升级会员入口
        } else {
          return '升级会员';
        }
      }
    } else {
      return '升级会员插队加速';
    }
  }, [isVip, remainedPriority, isSpecialUser]);

  const onInsertClick = useCallback(async () => {
    if (btnText === '插队加速') {
      // 对当前任务发起加速
      const result = await requestAccelerate(taskId);
      if (result) {
        notifySuccess(`加速成功`);
        loadVipInfo();
      } else {
        notifyError(`加速失败，请稍后重试`);
      }
    } else {
      updateVipState({
        proPurchaseModalVisible: true,
        currentErrorMsg: '加速次数不足',
      });
    }
  }, [btnText, taskId, loadVipInfo]);

  let remindTip = useMemo(() => {
    if (isVip) {
      if (remainedPriority) {
        return `本月插队特权剩余${remainedPriority}次`;
      } else {
        return `本月加速特权已用完`;
      }
    } else {
      return '';
    }
  }, [isVip, remainedPriority]);

  return {
    remindTip,
    btnText,
    onInsertClick,
  };
};
